import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { API } from '@/App';
import { 
  ArrowLeft, Users, Package, IndianRupee, TrendingDown, TrendingUp, 
  Award, CheckCircle, Eye, Grid, List, Building2
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function CompareVendorQuotes() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'

  useEffect(() => {
    fetchComparisonData();
  }, [requestId]);

  const fetchComparisonData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/vendor-quote-requests/${requestId}/compare`);
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load comparison data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '—';
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-stone-900" />
        </div>
      </Layout>
    );
  }

  if (!data || data.vendors?.length === 0) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto py-6">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft size={16} className="mr-2" /> Back
          </Button>
          <Card>
            <CardContent className="py-12 text-center text-stone-500">
              <Users size={48} className="mx-auto mb-4 text-stone-300" />
              <p>No vendor quotes submitted for this request yet.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto py-6 space-y-6" data-testid="compare-vendor-quotes">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft size={16} className="mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-stone-900">Compare Vendor Quotes</h1>
              <p className="text-stone-500">{data.quote_request?.project_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
            >
              <Grid size={14} className="mr-1" /> Grid
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('table')}
            >
              <List size={14} className="mr-1" /> Table
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Vendors</p>
                  <p className="text-2xl font-bold text-stone-900">{data.summary?.total_vendors}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingDown size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Lowest Total</p>
                  <p className="text-xl font-bold text-green-600">{formatCurrency(data.summary?.lowest_possible_total)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <TrendingUp size={20} className="text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Highest Total</p>
                  <p className="text-xl font-bold text-red-600">{formatCurrency(data.summary?.highest_possible_total)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Award size={20} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Potential Savings</p>
                  <p className="text-xl font-bold text-purple-600">{formatCurrency(data.summary?.potential_savings)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Best Vendor Highlight */}
        {data.summary?.best_overall_vendor && (
          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <Award size={24} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-green-700 font-medium">Best Overall Vendor</p>
                  <p className="text-lg font-bold text-green-900">
                    {data.summary.best_overall_vendor.vendor_name}
                    {data.summary.best_overall_vendor.vendor_company && (
                      <span className="font-normal text-green-700"> ({data.summary.best_overall_vendor.vendor_company})</span>
                    )}
                  </p>
                  <p className="text-green-600">Total: {formatCurrency(data.summary.best_overall_vendor.total)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Comparison View */}
        {viewMode === 'table' ? (
          /* Table View */
          <Card>
            <CardHeader>
              <CardTitle>Rate Comparison Table</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-stone-50">
                      <th className="text-left py-3 px-4 font-medium text-stone-600">Task</th>
                      <th className="text-center py-3 px-2 font-medium text-stone-600">Qty</th>
                      {data.vendors?.map(vendor => (
                        <th key={vendor.submission_id} className="text-right py-3 px-4 font-medium text-stone-600">
                          <div className="flex flex-col items-end">
                            <span>{vendor.vendor_name}</span>
                            {vendor.vendor_company && (
                              <span className="text-xs text-stone-400">{vendor.vendor_company}</span>
                            )}
                          </div>
                        </th>
                      ))}
                      <th className="text-right py-3 px-4 font-medium text-green-600">Lowest</th>
                      <th className="text-right py-3 px-4 font-medium text-red-600">Highest</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.comparison?.map((item, idx) => (
                      <tr key={item.task_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                        <td className="py-3 px-4">
                          <p className="font-medium text-stone-900">{item.title}</p>
                          {item.description && (
                            <p className="text-xs text-stone-500 line-clamp-1">{item.description}</p>
                          )}
                        </td>
                        <td className="text-center py-3 px-2 text-stone-600">
                          {item.quantity} {item.unit}
                        </td>
                        {item.vendor_rates?.map(vr => {
                          const isLowest = vr.rate === item.lowest_rate && vr.rate > 0;
                          const isHighest = vr.rate === item.highest_rate && vr.rate > 0;
                          return (
                            <td 
                              key={vr.submission_id} 
                              className={`text-right py-3 px-4 ${isLowest ? 'bg-green-50 text-green-700 font-semibold' : isHighest ? 'bg-red-50 text-red-600' : 'text-stone-700'}`}
                            >
                              {vr.rate > 0 ? formatCurrency(vr.rate) : '—'}
                            </td>
                          );
                        })}
                        <td className="text-right py-3 px-4 text-green-700 font-semibold">
                          {item.lowest_rate ? formatCurrency(item.lowest_rate) : '—'}
                        </td>
                        <td className="text-right py-3 px-4 text-red-600">
                          {item.highest_rate ? formatCurrency(item.highest_rate) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 bg-stone-100">
                      <td colSpan={2} className="py-3 px-4 font-bold text-stone-900">TOTALS</td>
                      {data.vendors?.map(vendor => (
                        <td key={vendor.submission_id} className="text-right py-3 px-4 font-bold text-stone-900">
                          {formatCurrency(vendor.total_amount)}
                        </td>
                      ))}
                      <td className="text-right py-3 px-4 font-bold text-green-700">
                        {formatCurrency(data.summary?.lowest_possible_total)}
                      </td>
                      <td className="text-right py-3 px-4 font-bold text-red-600">
                        {formatCurrency(data.summary?.highest_possible_total)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Grid View */
          <div className="space-y-4">
            {data.comparison?.map(item => (
              <Card key={item.task_id}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-base">{item.title}</CardTitle>
                      {item.description && (
                        <p className="text-sm text-stone-500 mt-1 line-clamp-2">{item.description}</p>
                      )}
                    </div>
                    <Badge variant="outline" className="ml-4">
                      {item.quantity} {item.unit}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {item.vendor_rates?.map(vr => {
                      const isLowest = vr.rate === item.lowest_rate && vr.rate > 0;
                      const isHighest = vr.rate === item.highest_rate && vr.rate > 0;
                      return (
                        <div 
                          key={vr.submission_id}
                          className={`p-3 rounded-lg border ${isLowest ? 'bg-green-50 border-green-300' : isHighest ? 'bg-red-50 border-red-200' : 'bg-stone-50 border-stone-200'}`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            {isLowest && <CheckCircle size={14} className="text-green-600" />}
                            <p className="font-medium text-stone-900 text-sm">{vr.vendor_name}</p>
                          </div>
                          {vr.vendor_company && (
                            <p className="text-xs text-stone-500 flex items-center gap-1 mb-1">
                              <Building2 size={10} /> {vr.vendor_company}
                            </p>
                          )}
                          <p className={`text-lg font-bold ${isLowest ? 'text-green-700' : isHighest ? 'text-red-600' : 'text-stone-700'}`}>
                            {vr.rate > 0 ? formatCurrency(vr.rate) : '—'}
                          </p>
                          {vr.rate > 0 && (
                            <p className="text-xs text-stone-500">
                              Total: {formatCurrency(vr.total)}
                            </p>
                          )}
                          {vr.remarks && (
                            <p className="text-xs text-stone-500 mt-1 italic">"{vr.remarks}"</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {item.savings_potential > 0 && (
                    <div className="mt-3 pt-3 border-t flex items-center justify-between text-sm">
                      <span className="text-stone-500">Rate Difference:</span>
                      <span className="text-purple-600 font-medium">
                        Potential Savings: {formatCurrency(item.savings_potential)}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
